import { useState } from 'react'
import { FileUpload } from '@navikt/ds-react/FileUpload'
import type { FileObject } from '@navikt/ds-react/FileUpload'
import { Provider } from '@navikt/ds-react/Provider'
import { en, nb } from '@navikt/ds-react/locales'
// The library's own styling, and only the parts this component needs. ⛔ THE
// GLOBAL SHEETS ARE DELIBERATELY LEFT OUT — `reset.css` and `baseline.css`
// restyle `html` and `body` for the whole portal. Everything below is scoped to
// the library's own `aksel-` classes and sits inside its own cascade layers.
import '@navikt/ds-css/dist/global/tokens.css'
import '@navikt/ds-css/dist/component/typography.css'
import '@navikt/ds-css/dist/component/button.css'
import '@navikt/ds-css/dist/component/form.css'
import { useLocale, useT } from 'flyt/lib/language/language'
import type { FileDropProps, FilePickedProps } from './FileDrop.types'

/**
 * file-drop — the portal's ONE file drop target, and the home of @navikt/ds-react.
 *
 * A labeled drop area with a browse button, taking exactly ONE file. It asks
 * the caller's policy about the file it was handed, reports an accepted file
 * up once, and otherwise stays open with the reason on show.
 *
 * ⚠️ WHERE THE ADOPTION STOPS: the library is confined to this folder and to
 * `AkselForm`, only the sheets this component needs are imported, and nothing
 * about it appears on the contract above.
 *
 * ⚠️ ONE FILE, AND THE READER IS TOLD WHY. The library hands over everything
 * that was dropped, so the count is judged here: more than one file uploads
 * nothing and says so in the field's own error region, because silently taking
 * the first of five is the failure a reader never notices. A single rejected
 * file is shown as ITSELF — its name, the caller's reason beside it, a control
 * to clear it.
 *
 * The library speaks Norwegian by default and English on request, so the
 * portal's active language is handed to it rather than restated here.
 */

/** The library's rejection carries the reason; a missing one falls back to the unit's own. */
const reasonOf = (rejected: FileObject, fallback: string): string =>
  (rejected.error ? rejected.reasons[0] : undefined) ?? fallback

/** A refused file, held only long enough to show the reader which one and why. */
interface Refusal {
  readonly name: string
  readonly size: number
  readonly reason: string
}

export function FileDrop({ label, description, validate, onFileAccepted }: FileDropProps) {
  const t = useT('common')
  const locale = useLocale()
  // The two ways an attempt can fail, kept apart because they are shown in
  // different places: a count is about the DROP (the field's error region), a
  // refusal is about a FILE (its own row). Both clear on the next attempt.
  const [refused, setRefused] = useState<Refusal | null>(null)
  const [tooMany, setTooMany] = useState(false)

  const handleSelect = (files: FileObject[]): void => {
    setRefused(null)
    setTooMany(false)
    if (files.length > 1) {
      setTooMany(true)
      return
    }
    const only = files[0]
    if (only === undefined) {
      return
    }
    if (only.error) {
      setRefused({
        name: only.file.name,
        size: only.file.size,
        reason: reasonOf(only, t('fileDrop.rejected')),
      })
      return
    }
    onFileAccepted(only.file)
  }

  return (
    <Provider locale={locale === 'en' ? en : nb}>
      <FileUpload>
        <FileUpload.Dropzone
          label={label}
          multiple={false}
          onSelect={handleSelect}
          {...(description === undefined ? {} : { description })}
          {...(validate === undefined
            ? {}
            : { validator: (file: File) => validate(file) ?? true })}
          {...(tooMany ? { error: t('fileDrop.tooMany') } : {})}
        />
        {refused === null ? null : (
          <FileUpload.Item
            file={{ name: refused.name, size: refused.size }}
            error={refused.reason}
            button={{
              action: 'delete',
              onClick: () => {
                setRefused(null)
              },
            }}
          />
        )}
      </FileUpload>
    </Provider>
  )
}

/**
 * file-picked — the file that WAS handed over, shown as itself with one way to
 * take it back.
 *
 * THE SAME ROW THE REFUSAL ABOVE USES, and deliberately so: a kept file and a
 * turned-away file are one object in two states, so they are drawn by one
 * component. It carries the name, the size and the library's own delete
 * control, which already speaks Norwegian and English.
 *
 * WHY IT IS NOT PART OF THE DROP TARGET. The reader is asked for a file ONCE,
 * at the top of a flow; the row that says which file they gave belongs wherever
 * the flow has got to since — a second screen, a form header, a summary. So it
 * is handed out on its own and the caller places it. ⚠️ It removes NOTHING: it
 * reports that the reader wants the file gone, and what that costs is the
 * caller's to know.
 */
export function FilePicked({ name, size, onRemove }: FilePickedProps) {
  const locale = useLocale()

  return (
    <Provider locale={locale === 'en' ? en : nb}>
      <FileUpload>
        <FileUpload.Item file={{ name, size }} button={{ action: 'delete', onClick: onRemove }} />
      </FileUpload>
    </Provider>
  )
}
